import {computed, Injectable, signal} from '@angular/core';
import {AudioTrack} from './audio.service';

/**
 * Service responsible for audio state management using Angular signals
 * Extracted from AudioService to improve maintainability while preserving exact functionality
 */
@Injectable({
  providedIn: 'root'
})
export class AudioStateService {

  // ============================================================================
  // SIGNAL DEFINITIONS - EXACT preservation from AudioService
  // ============================================================================

  readonly duration = computed(() => {
    const track = this.currentTrack();
    // For gaming mode (no endTime), use the actual audio duration from Howler
    if (track && track.endTime === undefined) {
      return this.actualAudioDuration(); // Will be set when audio loads
    }
    // For normal mode with defined start/end times
    if (track && track.endTime !== undefined && track.startTime !== undefined) {
      return track.endTime - track.startTime;
    }
    return 0;
  });
  // Core audio state signals
  private tracksSignal = signal<AudioTrack[]>([]);
  readonly tracks = this.tracksSignal.asReadonly();
  private currentTrackIndexSignal = signal<number>(0);
  readonly currentTrackIndex = this.currentTrackIndexSignal.asReadonly();
  readonly currentTrack = computed(() => {
    const tracks = this.tracksSignal();
    const index = this.currentTrackIndexSignal();
    return tracks.length > 0 ? tracks[index] : null;
  });
  readonly songTitle = computed(() => this.currentTrack()?.title || '');
  readonly artistName = computed(() => this.currentTrack()?.artist || '');
  readonly startTime = computed(() => this.currentTrack()?.startTime || 0);
  readonly endTime = computed(() => {
    const track = this.currentTrack();
    return track?.endTime; // Return undefined if not set (for gaming mode full song playback)
  });
  readonly briefDescription = computed(() => this.currentTrack()?.briefDescription || '');
  private isPlayingSignal = signal<boolean>(false);
  readonly isPlaying = this.isPlayingSignal.asReadonly();
  private isLoadingSignal = signal<boolean>(false);

  // ============================================================================
  // READ-ONLY COMPUTED PROPERTIES - EXACT preservation
  readonly isLoading = this.isLoadingSignal.asReadonly();
  // ============================================================================
  private volumeSignal = signal<number>(0.5); // Default 50% volume
  readonly volume = this.volumeSignal.asReadonly();
  private currentTimeSignal = signal<number>(0);
  readonly currentTime = this.currentTimeSignal.asReadonly();
  private actualAudioDurationSignal = signal<number>(0);
  readonly actualAudioDuration = this.actualAudioDurationSignal.asReadonly();
  // Track lifecycle and operation state signals
  private currentTrackLoadId = signal<number>(0);
  private isPauseResumeOperation = signal<boolean>(false);
  private isLoopingTrack = signal<boolean>(false);
  private wasUserInitiated = signal<boolean>(false);
  private lastUserInteractionTime = signal<number>(0);
  // Autoplay and failure tracking signals
  private autoplayFailures = signal<number>(0);
  private isAutoplayBlockedSignal = signal<boolean>(false);
  readonly isAutoplayBlocked = this.isAutoplayBlockedSignal.asReadonly();

  // ============================================================================
  // STATE MANAGEMENT METHODS - EXACT preservation
  // ============================================================================

  /**
   * Set the audio tracks and reset the player state
   * EXACT preservation from AudioService.setTracks()
   */
  setTracks(tracks: AudioTrack[]): void {
    this.tracksSignal.set(tracks);
    this.currentTrackIndexSignal.set(0);
    this.isPlayingSignal.set(false);
    this.currentTimeSignal.set(0);
    // Duration is now computed automatically from the current track
  }

  /**
   * Update current track index
   */
  setCurrentTrackIndex(index: number): void {
    this.currentTrackIndexSignal.set(index);
  }

  /**
   * Update playing state
   */
  setIsPlaying(playing: boolean): void {
    this.isPlayingSignal.set(playing);
  }

  /**
   * Update loading state
   */
  setIsLoading(loading: boolean): void {
    this.isLoadingSignal.set(loading);
  }

  /**
   * Update volume with validation
   */
  setVolume(volume: number): void {
    this.volumeSignal.set(Math.max(0, Math.min(1, volume)));
  }

  /**
   * Update current playback time
   */
  setCurrentTime(time: number): void {
    this.currentTimeSignal.set(time);
  }

  setActualAudioDuration(duration: number): void {
    this.actualAudioDurationSignal.set(duration);
  }

  /**
   * Update track duration
   * NOTE: Duration is now automatically computed from currentTrack's endTime - startTime
   * This method is kept for compatibility but doesn't do anything
   */
  setDuration(duration: number): void {
    // Duration is now computed automatically from the current track
    // This method is kept for compatibility but no longer needed
  }

  // ============================================================================
  // INTERNAL STATE ACCESSORS - For other audio services
  // ============================================================================

  getCurrentTrackLoadId(): number {
    return this.currentTrackLoadId();
  }

  incrementTrackLoadId(): number {
    const newId = this.currentTrackLoadId() + 1;
    this.currentTrackLoadId.set(newId);
    return newId;
  }

  getIsPauseResumeOperation(): boolean {
    return this.isPauseResumeOperation();
  }

  setIsPauseResumeOperation(value: boolean): void {
    this.isPauseResumeOperation.set(value);
  }

  getIsLoopingTrack(): boolean {
    return this.isLoopingTrack();
  }

  setIsLoopingTrack(value: boolean): void {
    this.isLoopingTrack.set(value);
  }

  getWasUserInitiated(): boolean {
    return this.wasUserInitiated();
  }

  setWasUserInitiated(value: boolean): void {
    this.wasUserInitiated.set(value);
  }

  getLastUserInteractionTime(): number {
    return this.lastUserInteractionTime();
  }

  setLastUserInteractionTime(time: number): void {
    this.lastUserInteractionTime.set(time);
  }

  getAutoplayFailures(): number {
    return this.autoplayFailures();
  }

  incrementAutoplayFailures(): void {
    this.autoplayFailures.set(this.autoplayFailures() + 1);
  }

  setIsAutoplayBlocked(blocked: boolean): void {
    this.isAutoplayBlockedSignal.set(blocked);
  }

  /**
   * Reset autoplay failure count
   */
  resetAutoplayFailures(): void {
    this.autoplayFailures.set(0);
  }
} 
